import React, { useState, useRef } from 'react';
import { PencilLine, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { useApp } from '../context/AppContext';
import { getAnalysisChartUrl, submitUserAnnotation, showFriendlyApiError } from '../lib/api';
import { buildPayload } from '../types/annotation';
import AnnotationCanvas from '../components/annotation/AnnotationCanvas';

export default function Annotate() {
  const { currentAnalysis, currentProject } = useApp();
  const [localImageUrl, setLocalImageUrl] = useState(null);
  const [nodes, setNodes] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [lastGrade, setLastGrade] = useState(null);
  const [lastError, setLastError] = useState(null);
  const fileRef = useRef(null);

  const chartUrl = currentAnalysis?.id
    ? getAnalysisChartUrl(currentAnalysis.id, { view: 'raw' })
    : localImageUrl;

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    setLocalImageUrl(url);
  };

  const handleSubmitForGrading = async () => {
    if (!currentAnalysis?.id) {
      toast.error('No active analysis to submit against');
      return;
    }
    if (nodes.length === 0) {
      toast.error('Place at least one wave node before submitting');
      return;
    }
    setSubmitting(true);
    setLastError(null);
    try {
      const payload = buildPayload(nodes, 0, 0, currentAnalysis.id, currentProject?.id);
      const result = await submitUserAnnotation(currentAnalysis.id, payload);
      const grade = result?.grade || result?.annotation?.grade || null;
      setLastGrade(grade);
      if (grade?.status === 'deferred') {
        toast.message('Annotation saved — grade deferred', {
          description: grade.message || 'Engine has no reference pivots to grade against.',
        });
      } else if (grade?.score_available && grade?.score != null) {
        toast.success(`Graded ${Number(grade.score).toFixed(1)}/100`, {
          description: grade.message || 'Annotation persisted and graded.',
        });
      } else {
        toast.success('Annotation submitted', {
          description: 'Saved to backend.',
        });
      }
    } catch (err) {
      setLastError(err?.message || 'Failed to submit annotation');
      showFriendlyApiError(err, 'submit annotation');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in text-[#d8dae0]">
      <div className="relative overflow-hidden rounded-xl border border-[#ffffff0a] bg-[#090b10] p-6 shadow-2xl">
        <div className="absolute top-0 left-0 h-[2px] w-1/3 bg-gradient-to-r from-transparent via-[#f5b400] to-transparent opacity-70" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(245,180,0,0.04),_transparent_40%)]" />
        <div className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <div className="mb-3 flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-[#f5b400]">
              <PencilLine className="w-3.5 h-3.5" />
              User Annotation Canvas
            </div>
            <h1 className="text-2xl font-bold tracking-tight text-white mb-1">
              Manual Wave Count
            </h1>
            <p className="text-xs text-[#8b93a7] font-mono">
              Drop wave nodes on the chart and submit your count for the engine to grade.
              {currentAnalysis?.id
                ? ` Active analysis: ${currentAnalysis.id.slice(0, 8)}...`
                : ' Upload a chart image to begin. Grading requires an active analysis.'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {!currentAnalysis?.id && (
              <>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleFileUpload}
                />
                <button
                  onClick={() => fileRef.current?.click()}
                  className="flex items-center gap-1.5 px-4 py-2 rounded text-[11px] font-mono font-medium bg-white/5 text-white border border-white/10 hover:bg-white/10 transition-all"
                >
                  <Upload className="w-3 h-3" /> Upload Chart
                </button>
              </>
            )}
            {currentAnalysis?.id && (
              <button
                onClick={handleSubmitForGrading}
                disabled={submitting || nodes.length === 0}
                data-testid="submit-annotation"
                className="flex items-center gap-1.5 px-4 py-2 rounded text-[11px] font-mono font-medium bg-[#f5b400]/10 text-[#f5b400] border border-[#f5b400]/30 hover:bg-[#f5b400]/20 transition-all disabled:opacity-50"
              >
                {submitting ? 'Submitting…' : 'Submit for Grading'}
              </button>
            )}
          </div>
        </div>
      </div>

      {lastGrade && (
        <div
          data-testid="grade-panel"
          className="rounded-xl border border-[#ffffff0a] bg-[#0c0e14] p-4 space-y-2"
        >
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#f5b400]">
            Grade Result
          </div>
          {lastGrade.score_available && lastGrade.score != null ? (
            <div className="text-2xl font-mono font-bold text-white">
              {Number(lastGrade.score).toFixed(1)}
              <span className="text-sm text-[#8b93a7] font-normal"> / 100</span>
            </div>
          ) : (
            <div className="text-sm text-[#f5b400] font-mono">
              {lastGrade.status === 'deferred' ? 'Deferred' : 'Unavailable'}
            </div>
          )}
          {lastGrade.message && (
            <p className="text-xs text-[#8b93a7] font-mono">{lastGrade.message}</p>
          )}
          {Array.isArray(lastGrade.rule_notes) && lastGrade.rule_notes.length > 0 && (
            <ul className="text-[11px] text-[#8b93a7] font-mono space-y-1 list-disc list-inside">
              {lastGrade.rule_notes.map((note, i) => (
                <li key={i}>{note}</li>
              ))}
            </ul>
          )}
          {lastGrade.components && (
            <div className="flex flex-wrap gap-3 pt-1 text-[10px] font-mono text-[#6b7280]">
              {Object.entries(lastGrade.components)
                .filter(([k]) => k !== 'weights')
                .map(([k, v]) => (
                  <span key={k}>
                    {k}: {typeof v === 'number' ? v.toFixed(1) : String(v)}
                  </span>
                ))}
            </div>
          )}
        </div>
      )}

      {lastError && (
        <div
          data-testid="annotate-error"
          className="rounded border border-red-500/30 bg-red-500/5 px-3 py-2 text-xs font-mono text-red-300"
        >
          {lastError}
        </div>
      )}

      <AnnotationCanvas
        chartImageUrl={chartUrl}
        analysisId={currentAnalysis?.id}
        projectId={currentProject?.id}
        onNodesChange={setNodes}
      />
    </div>
  );
}
